import { useEffect, useState } from "react";
import { fr } from "date-fns/locale";
import { CalendarClock, Loader2 } from "lucide-react";
import { Calendar } from "../ui/calendar";
import { api } from "../../lib/api";

const MAX_DAYS = 60;
export const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const fromIso = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
export const humanDate = (iso, time) => {
  if (!iso) return "";
  const d = fromIso(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return time ? `${d} à ${time.replace(":", "h")}` : d;
};

export function useSlots(date, { admin = false, exclude, refresh = 0 } = {}) {
  const [state, setState] = useState({ loading: false, slots: [], closed: false, error: "" });
  useEffect(() => {
    if (!date) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: "" }));
    const url = admin ? "/admin/slots" : "/slots";
    api.get(url, { params: { date, ...(exclude ? { exclude } : {}) } })
      .then((r) => { if (alive) setState({ loading: false, slots: Array.isArray(r.data?.slots) ? r.data.slots : [], closed: Boolean(r.data?.closed), error: "" }); })
      .catch(() => { if (alive) setState({ loading: false, slots: [], closed: false, error: "Impossible de charger les créneaux. Appelez-nous au 03 88 93 16 73." }); });
    return () => { alive = false; };
  }, [date, admin, exclude, refresh]);
  return state;
}

export function SlotGrid({ slots, value, onChange, testid = "slot" }) {
  return (
    <div className="grid grid-cols-4 gap-2 sm:grid-cols-6" role="listbox" aria-label="Horaires disponibles">
      {slots.map((t) => (
        <button key={t} type="button" role="option" aria-selected={value === t} onClick={() => onChange(t)}
          className={`rounded-xl px-2 py-2.5 font-mono text-sm font-semibold transition-colors ${value === t ? "bg-ag-blue text-white shadow-md" : "bg-white text-ag-ink ring-1 ring-slate-200 hover:ring-ag-blue"}`}
          data-testid={`${testid}-${t.replace(":", "")}`}>
          {t.replace(":", "h")}
        </button>
      ))}
    </div>
  );
}

export function SlotPicker({ date, time, onDate, onTime, refresh }) {
  const { loading, slots, closed, error } = useSlots(date, { refresh });
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const max = new Date(today);
  max.setDate(max.getDate() + MAX_DAYS);
  useEffect(() => {
    if (time && !loading && date && !slots.includes(time)) onTime("");
  }, [slots, loading]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="grid gap-5 sm:col-span-2 lg:grid-cols-[auto_1fr]" data-testid="slot-picker">
      <div className="rounded-2xl bg-ag-mist p-2 ring-1 ring-ag-blue/10">
        <Calendar mode="single" locale={fr} weekStartsOn={1} selected={date ? fromIso(date) : undefined}
          onSelect={(d) => { if (d) { onDate(toIso(d)); onTime(""); } }}
          disabled={[{ before: today }, { after: max }, { dayOfWeek: [0] }]} fromDate={today} toDate={max}
          data-testid="slot-calendar" />
      </div>
      <div>
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><CalendarClock className="h-4 w-4 text-ag-blue" />
          {date ? `Horaires disponibles le ${humanDate(date)}` : "Choisissez d'abord une date (fermé le dimanche)"}
        </p>
        {loading && <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Chargement des créneaux…</p>}
        {error && <p className="text-sm text-red-600" data-testid="slot-error">{error}</p>}
        {!loading && date && !error && slots.length === 0 && (
          <p className="rounded-xl bg-white p-4 text-sm text-slate-600 ring-1 ring-slate-200" data-testid="slot-none">{closed ? "Le centre est fermé ce jour-là." : "Aucun créneau disponible ce jour-là. Merci de choisir une autre date."}</p>
        )}
        {!loading && slots.length > 0 && <SlotGrid slots={slots} value={time} onChange={onTime} />}
      </div>
    </div>
  );
}
